import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { OWNER_SHARE_PERCENT } from './rentalPayments';

const CONFIRM_PAYMENT_URL =
  'https://us-central1-attackyouradventure-ee7a6.cloudfunctions.net/confirmRentalPayment';

function Success() {
  const [status, setStatus] = useState({
    loading: true,
    error: '',
    gearId: '',
    gearName: '',
    scheduled: false,
    chargeDueAt: '',
  });

  useEffect(() => {
    const sessionId = new URLSearchParams(window.location.search).get('session_id');
    if (!sessionId) {
      setStatus({ loading: false, error: '', gearId: '', gearName: '', scheduled: false, chargeDueAt: '' });
      return;
    }

    const confirmPayment = async () => {
      try {
        const response = await fetch(CONFIRM_PAYMENT_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ sessionId }),
        });

        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(data.error || 'Could not confirm payment.');
        }

        setStatus({
          loading: false,
          error: '',
          gearId: data.gearId || '',
          gearName: data.gearName || '',
          scheduled: Boolean(data.scheduled || data.alreadyScheduled),
          chargeDueAt: data.chargeDueAt || '',
        });
      } catch (error) {
        setStatus({
          loading: false,
          error: error.message || 'Could not confirm payment.',
          gearId: '',
          gearName: '',
          scheduled: false,
          chargeDueAt: '',
        });
      }
    };

    confirmPayment();
  }, []);

  const chargeDueLabel = status.chargeDueAt
    ? new Date(status.chargeDueAt).toLocaleDateString('en-US', {
        day: '2-digit',
        month: 'short',
        year: '2-digit',
        hour: 'numeric',
        minute: '2-digit',
      })
    : '';

  return (
    <div style={{
      maxWidth: '600px',
      margin: '100px auto',
      textAlign: 'center',
      padding: '40px',
    }}>
      {status.loading ? (
        <>
          <h1 style={{ color: '#3b82f6' }}>Confirming...</h1>
          <p style={{ fontSize: '1.1rem', marginTop: '20px', color: '#6b7280' }}>
            Hang tight while we finalize your rental payment details.
          </p>
        </>
      ) : status.error ? (
        <>
          <h1 style={{ color: '#ef4444' }}>Confirmation Issue</h1>
          <p style={{ fontSize: '1.1rem', marginTop: '20px', color: '#6b7280' }}>
            {status.error}
          </p>
          <Link
            to="/my-bookings"
            style={{
              display: 'inline-block',
              marginTop: '30px',
              padding: '12px 30px',
              backgroundColor: '#3b82f6',
              color: 'white',
              textDecoration: 'none',
              borderRadius: '8px',
            }}
          >
            View My Bookings
          </Link>
        </>
      ) : status.scheduled ? (
        <>
          <h1 style={{ color: '#10b981' }}>Card Saved</h1>
          <p style={{ fontSize: '1.2rem', marginTop: '20px' }}>
            Your rental is secured. Your card will be charged automatically before the rental begins.
          </p>
          {status.gearName && (
            <p style={{ color: '#6b7280' }}>
              {status.gearName} is scheduled for payment{chargeDueLabel ? ` on ${chargeDueLabel}` : ' 48 hours before your rental starts'}.
            </p>
          )}
          <p style={{ color: '#6b7280', fontSize: '0.95rem', lineHeight: '1.6', marginTop: '16px' }}>
            If you cancel before the charge date, your card will not be billed. After the charge, the gear owner receives {OWNER_SHARE_PERCENT}% of the rental total.
          </p>
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap', marginTop: '30px' }}>
            {status.gearId && (
              <Link
                to={`/gear/${status.gearId}#rental-payment`}
                style={{
                  display: 'inline-block',
                  padding: '12px 30px',
                  backgroundColor: '#10b981',
                  color: 'white',
                  textDecoration: 'none',
                  borderRadius: '8px',
                  fontWeight: '600',
                }}
              >
                View Rental
              </Link>
            )}
            <Link
              to="/my-bookings"
              style={{
                display: 'inline-block',
                padding: '12px 30px',
                backgroundColor: '#3b82f6',
                color: 'white',
                textDecoration: 'none',
                borderRadius: '8px',
                fontWeight: '600',
              }}
            >
              My Bookings
            </Link>
          </div>
        </>
      ) : (
        <>
          <h1 style={{ color: '#10b981' }}>Payment Successful!</h1>
          <p style={{ fontSize: '1.2rem', marginTop: '20px' }}>
            Thank you! Your rental payment has been recorded.
          </p>
          {status.gearName && (
            <p style={{ color: '#6b7280' }}>
              {status.gearName} is now marked as <strong>paid</strong>.
            </p>
          )}
          <p style={{ color: '#6b7280', fontSize: '0.95rem', lineHeight: '1.6', marginTop: '16px' }}>
            The gear owner receives {OWNER_SHARE_PERCENT}% of your payment when the charge processes (around 48 hours before your rental starts, or right away if you booked inside that window).
            If this listing has a condition guarantee, a separate authorization may have been placed on your card.
            The owner releases that hold after the gear is returned in acceptable condition.
          </p>
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap', marginTop: '30px' }}>
            {status.gearId && (
              <Link
                to={`/gear/${status.gearId}#rental-payment`}
                style={{
                  display: 'inline-block',
                  padding: '12px 30px',
                  backgroundColor: '#10b981',
                  color: 'white',
                  textDecoration: 'none',
                  borderRadius: '8px',
                  fontWeight: '600',
                }}
              >
                View Rental
              </Link>
            )}
            <Link
              to="/my-bookings"
              style={{
                display: 'inline-block',
                padding: '12px 30px',
                backgroundColor: '#3b82f6',
                color: 'white',
                textDecoration: 'none',
                borderRadius: '8px',
                fontWeight: '600',
              }}
            >
              My Bookings
            </Link>
          </div>
        </>
      )}
    </div>
  );
}

export default Success;